import { AccountFee, AccountFeeDetail, BillingGroupRun, FeeType, PaymentStatus, RunStatus } from '@ndvr/billing-api';

export const parseAmount = (amount: string) => parseFloat(amount);
const parseOptionalAmount = (amount?: string) => (amount == null ? undefined : parseAmount(amount));
export const accountLabel = (count: number) => `${count} ${count === 1 ? 'account' : 'accounts'}`;

// Group and account rows expose their aggregated values through the same `rollup` field.
export type BillingRunTableRow =
    | {
          id: string;
          kind: 'group';
          run: BillingGroupRun;
          rollup: BillingGroupRun;
          subRows?: BillingRunTableRow[];
      }
    | {
          id: string;
          kind: 'account';
          run: BillingGroupRun;
          fee: AccountFee;
          rollup: AccountFee;
          accountName: string;
          subRows?: BillingRunTableRow[];
      }
    | {
          id: string;
          kind: 'detail';
          run: BillingGroupRun;
          fee: AccountFee;
          detail: AccountFeeDetail;
          subRows?: undefined;
      };

export const FEE_TYPE_BADGE_LABEL: Record<FeeType, string> = {
    [FeeType.ADVISORY]: 'Advisory',
    [FeeType.BOOST]: 'Boost',
    [FeeType.INTELLIGENT_STRATEGY]: 'IS',
    [FeeType.AUM]: 'AUM',
    [FeeType.MANAGEMENT]: 'Mgmt',
};
export const FEE_TYPE_DESCRIPTION: Record<FeeType, string> = {
    [FeeType.ADVISORY]: 'Advisory fee',
    [FeeType.BOOST]: 'Portfolio Boost',
    [FeeType.INTELLIGENT_STRATEGY]: 'Intelligent Strategies',
    [FeeType.AUM]: 'AUM',
    [FeeType.MANAGEMENT]: 'Management fee',
};
export const FEE_TYPE_BADGE_CLASSNAME: Record<FeeType, string> = {
    [FeeType.ADVISORY]: 'border-transparent bg-seafoam-100 text-seafoam-600',
    [FeeType.BOOST]: 'border-transparent bg-gold-100 text-gold-700',
    [FeeType.INTELLIGENT_STRATEGY]: 'border-transparent bg-purple-100 text-purple-700',
    [FeeType.AUM]: '',
    [FeeType.MANAGEMENT]: '',
};

// Deprecated fee types remain here so historical fee details still render.
export const ACTIVE_FEE_TYPES = [FeeType.ADVISORY, FeeType.BOOST, FeeType.INTELLIGENT_STRATEGY];

export const amendableFeeTypes = (run: BillingGroupRun) =>
    ACTIVE_FEE_TYPES.filter((type) =>
        run.accountFees.some((fee) =>
            fee.feeDetails.some(
                (detail) =>
                    detail.feeType === type && (type !== FeeType.INTELLIGENT_STRATEGY || detail.strategyType != null),
            ),
        ),
    );

const billingGroupName = (run: BillingGroupRun) => run.billingGroupName ?? run.billingGroupGUID;

export const primaryLabel = (row: BillingRunTableRow) =>
    row.kind === 'group'
        ? billingGroupName(row.run)
        : row.kind === 'account'
          ? row.accountName
          : FEE_TYPE_DESCRIPTION[row.detail.feeType];

export const totalAum = (row: BillingRunTableRow) => (row.kind === 'detail' ? undefined : parseAmount(row.rollup.aum));
export const excludedAum = (row: BillingRunTableRow) =>
    row.kind === 'detail' ? undefined : parseAmount(row.rollup.excludedAum);
export const currentPeriodFee = (row: BillingRunTableRow) =>
    row.kind === 'detail' ? parseAmount(row.detail.chargedAmount) : parseAmount(row.rollup.chargedAmount);
// Outstanding rollups include earlier arrears, which individual fee details do not contain.
export const totalOutstanding = (row: BillingRunTableRow) =>
    row.kind === 'detail' ? undefined : parseAmount(row.rollup.totalOutstandingFee);
export const cashEncumberedForRow = (row: BillingRunTableRow) =>
    row.kind === 'detail' ? undefined : parseOptionalAmount(row.rollup.cashEncumbered);
export const cashShortfallForRow = (row: BillingRunTableRow) =>
    row.kind === 'detail' ? undefined : parseOptionalAmount(row.rollup.cashShortfall);
const paymentStatusForRow = (row: BillingRunTableRow): PaymentStatus =>
    row.kind === 'detail' ? row.detail.paymentStatus : row.rollup.paymentStatus;
// Empty failed runs otherwise roll up to PAID and appear incorrectly as settled.
export const runFailed = (row: BillingRunTableRow) => row.run.runStatus === RunStatus.FAILED;

export const PAYMENT_STATUS_LABEL: Record<PaymentStatus, string> = {
    [PaymentStatus.PAID]: 'Settled',
    [PaymentStatus.PARTIALLY_PAID]: 'Partial',
    [PaymentStatus.UNPAID]: 'Unpaid',
    [PaymentStatus.CREDIT_DUE]: 'Credit due',
};
export const PAYMENT_STATUS_CLASSNAME: Record<PaymentStatus, string> = {
    [PaymentStatus.PAID]: 'border-transparent bg-green-100 text-green-800',
    [PaymentStatus.PARTIALLY_PAID]: 'bg-gold-100 text-gold-800 border-transparent',
    [PaymentStatus.UNPAID]: 'border-transparent bg-red-100 text-red-800',
    [PaymentStatus.CREDIT_DUE]: 'border-transparent bg-seafoam-100 text-seafoam-600',
};

// Include failure in the value used for settlement display, sorting, and filtering.
export const SETTLEMENT_FAILED = 'FAILED';
export type SettlementStatus = PaymentStatus | typeof SETTLEMENT_FAILED;
export const settlementStatusForRow = (row: BillingRunTableRow): SettlementStatus =>
    runFailed(row) ? SETTLEMENT_FAILED : paymentStatusForRow(row);
export const SETTLEMENT_STATUS_LABEL: Record<SettlementStatus, string> = {
    [SETTLEMENT_FAILED]: 'Failed',
    ...PAYMENT_STATUS_LABEL,
};
// Hide failed runs from the default settlement filter.
export const DEFAULT_SETTLEMENT_STATUS_FILTER: string[] = Object.values(PaymentStatus);
export const SETTLEMENT_FILTER_OPTIONS = [SETTLEMENT_FAILED, ...Object.values(PaymentStatus)];
export const SETTLEMENT_STATUS_CLASSNAME: Record<SettlementStatus, string> = {
    [SETTLEMENT_FAILED]: 'border-transparent bg-red-100 text-red-800',
    ...PAYMENT_STATUS_CLASSNAME,
};
